import { set, action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { toDate, format } from 'date-fns';
import type Link from 'ember-link/link';
import type LinkManagerService from 'ember-link/services/link-manager';
import type AnnouncementModel from 'garaje/models/announcement';
import type TakeoverModel from 'garaje/models/takeover';
import type CurrentAdminService from 'garaje/services/current-admin';
import type ReactRendererService from 'garaje/services/react-renderer';
import type { Root } from 'react-dom/client';
import { tracked } from 'tracked-built-ins';

interface MessageLogShowArgs {
  announcement: AnnouncementModel;
  backLink: string;
}

export default class CommunicationsMessageLogShowComponent extends Component<MessageLogShowArgs> {
  @service declare reactRenderer: ReactRendererService;
  @service declare currentAdmin: CurrentAdminService;
  @service linkManager!: LinkManagerService;

  private root?: Root;

  get backLink(): Link {
    return this.linkManager.createUILink({
      route: this.args.backLink,
    });
  }

  @action
  async injectComponent(): Promise<void> {
    const { ControlCenter } = await import('reactRichComponents/communication/ControlCenter');

    const emnoSettings = await this.args.announcement.emergencyNotification;

    const announcement = {
      id: this.args.announcement.id,
      title: this.args.announcement.title,
      message: this.args.announcement.message,
      type: emnoSettings ? 'emergency_notification' : 'announcement',
      locationName: this.args.announcement.location.content?.name,
      creatorName: this.args.announcement.creatorEmployee.content?.name,
      creationDate: this.args.announcement.createdAt.toISOString(),
      responseOptions: this.args.announcement.announcementResponseOptions.content.map((option) => ({
        id: option.id,
        label: option.label,
        category: option.category,
        order: option.order,
      })),
      takeoverStart: this.args.announcement.takeovers.content[0]?.startAt
        ? new Date(this.args.announcement.takeovers.content[0].startAt * 1000).toISOString()
        : null,
      takeoverEnd: this.args.announcement.takeovers.content[0]?.endAt
        ? new Date(this.args.announcement.takeovers.content[0].endAt * 1000).toISOString()
        : null,
      channels: this.args.announcement.announcementChannels.content.map((channel) => channel.channel),
      audiences: this.args.announcement.announcementAudiences.content.map((audience) => ({
        audienceName: audience.employeeAudience || audience.visitorAudience,
        size: 0,
      })),
      attachments: this.args.announcement.announcementAttachments.content.map((attachment) => ({
        id: attachment.id,
        name: attachment.rasterImageName,
        url: attachment.envoyImageUrl,
      })),
      rollCallEnabled: emnoSettings?.rollCallRequired || false,
      twoWayChatEnabled: emnoSettings?.twoWayChatRequired || false,
    };

    this.root = await this.reactRenderer.render(ControlCenter, 'react__communications__announcement-overview', {
      announcement,
      currentEmployeeId: this.currentAdmin.employee?.id,
      backLink: this.backLink.url,
      incidentId: this.args.announcement.incidentId ?? undefined,
      onTakeoverSlotReady: (el: HTMLDivElement) => {
        this.takeoverSlotElement = el;
      },
    });
  }

  @action
  destroyComponent(): void {
    if (this.root) {
      this.reactRenderer.unmount(this.root);
      this.root = undefined;
    }
    this.takeoverSlotElement = null;
  }

  @tracked takeoverSlotElement: Element | null = null;
  @tracked takeovers: TakeoverModel[] = [];

  async getTakeover(): Promise<void> {
    const takeovers = await this.args.announcement.takeovers;
    set(this, 'takeovers', takeovers ? takeovers.slice() : []);
  }

  get isLive(): boolean {
    return this.takeovers.some((takeover) => takeover.active);
  }

  get sentDate(): string {
    const createdAt = this.args.announcement.createdAt;
    const date = toDate(createdAt);

    return format(date, "MMM dd, yyyy 'at' h:mm a zzz");
  }
}
